import axios, { AxiosInstance, AxiosError } from 'axios';
import { Result, ok, err } from '@/domain/result';

// Shape for API errors
export interface ApiErrorMeta {
  status?: number;
  url?: string;
  method?: string;
}
export interface ApiError {
  message: string;
  meta?: ApiErrorMeta;
  original?: unknown;
  toString(): string;
}

function createClient(): AxiosInstance {
  const baseURL = process.env.VUE_APP_API_BASE_URL || '/api/';
  // Allow opting into credential mode only when explicitly needed to reduce CORS surface.
  const withCreds = (process.env.VUE_APP_WITH_CREDENTIALS || '').toLowerCase() === 'true';
  const instance = axios.create({
    baseURL,
    timeout: 30000,
    withCredentials: withCreds,
  });

  instance.interceptors.request.use((config) => {
    // Placeholder for auth token injection or correlation IDs
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError | undefined) => {
      // Handle rare edge where interceptor receives undefined/null
      if (!error) {
        const unknown = new Error('Unknown API error') as Error & ApiError;
        unknown.meta = { status: undefined, url: undefined, method: undefined };
        unknown.original = undefined;
        unknown.toString = function () { return this.message; };
        return Promise.reject(unknown);
      }

      // Enrich the original AxiosError instead of wrapping so downstream catch(err).response works
      const axErr = error as AxiosError & ApiError;
      // Attempt to surface server-provided message from common ProblemDetails / custom payloads.
      const data: any = axErr.response?.data;
      let serverMsg: string | undefined;
      if (data) {
        // Priority order: explicit message -> detail -> title -> errors aggregate -> raw string
        if (typeof data.message === 'string' && data.message.trim()) serverMsg = data.message.trim();
        else if (typeof data.detail === 'string' && data.detail.trim()) serverMsg = data.detail.trim();
        else if (typeof data.title === 'string' && data.title.trim()) serverMsg = data.title.trim();
        else if (data.errors && typeof data.errors === 'object') {
          // Aggregate validation errors
            try {
              const parts: string[] = [];
              Object.keys(data.errors).forEach(k => {
                const val = data.errors[k];
                if (Array.isArray(val)) parts.push(...val.filter(v => typeof v === 'string'));
                else if (typeof val === 'string') parts.push(val);
              });
              if (parts.length) serverMsg = parts.join('\n');
            } catch (_) { /* ignore */ }
        } else if (typeof data === 'string' && data.trim()) serverMsg = data.trim();
      }
      axErr.meta = {
        status: axErr.response?.status,
        url: axErr.config?.url,
        method: axErr.config?.method,
      };
      axErr.original = error; // keep original reference
      axErr.toString = function () { return this.message; };
      // Ensure message fallback
      if (serverMsg) {
        // Preserve original axios message in case debugging is needed
        (axErr as any).axiosMessage = axErr.message;
        (axErr as any).message = serverMsg;
      } else if (!axErr.message) {
        (axErr as any).message = 'Request failed';
      }
      return Promise.reject(axErr);
    }
  );

  return instance;
}

export const apiClient = createClient();

// Helper to wrap a promise into Result
export async function toResult<T>(p: Promise<T>): Promise<Result<ApiError, T>> {
  try {
    const value = await p;
    return ok(value);
  } catch (e: any) {
    return err(e as ApiError);
  }
}
